import { z } from 'zod';
import { apiClient } from '../client';

/**
 * "Renseigner un rendez-vous pris" (Kevin, 2026-09-23).
 *
 * The single call both screens make: the prospect card, where the caller
 * already has the prospect in front of them, and the left-nav entry, where
 * the prospect is picked from a list. Everything after the prospect id is
 * identical, which is why there is one function here and not two.
 *
 * The meeting is always saved in Revcall. `crmOutcome` says whether it also
 * left for the client's Swoft CRM, and why it did not when it did not:
 *   sent                 queued on the call it was obtained during
 *   saved_only_no_crm    that client has no Swoft CRM connected
 *   saved_only_no_call   the prospect has never been called to the end, and
 *                        Revcall has no way to push a meeting on its own
 */

export const BookedMeetingResultSchema = z.object({
  meetingId: z.string(),
  provider: z.enum(['none', 'hubspot', 'swoft']),
  customerName: z.string(),
  crmOutcome: z.enum(['sent', 'saved_only_no_crm', 'saved_only_no_call']),
  status: z.enum(['not_configured', 'not_sent', 'pending', 'delivered', 'failed', 'dead']).optional(),
  lastError: z.string().optional(),
  callId: z.string().optional(),
});

export type BookedMeetingResult = z.infer<typeof BookedMeetingResultSchema>;

/** The request body, built by buildBookedMeetingRequest in services/dialer. */
export interface BookedMeetingRequest {
  prospect_id: string;
  /** Date and time of the meeting, ISO 8601. */
  meeting_at: string;
  /** Local "HH:MM" as shown on screen. */
  meeting_time: string;
  duration_minutes: number;
  notes: string;
  /** The call the meeting was obtained on; the server checks it is this prospect's. */
  parallel_call_id?: string;
}

export async function recordBookedMeeting(request: BookedMeetingRequest): Promise<BookedMeetingResult> {
  const raw = await apiClient.post<unknown>('/integrations/meetings/booked', request);
  return BookedMeetingResultSchema.parse(raw);
}

export const BookedOnLinkResultSchema = z.object({
  /** Faux quand le prospect n'a aucun appel termine a porter la coche. */
  marked: z.boolean(),
  callId: z.string().optional(),
});

export type BookedOnLinkResult = z.infer<typeof BookedOnLinkResultSchema>;

/**
 * Le rendez-vous a ete pris sur le lien du client (Kevin, 2026-09-30).
 *
 * Sans CRM branche, l'appelant n'ouvre aucun formulaire : le lien s'ouvre et
 * le rendez-vous est marque comme obtenu. Sa date nous echappe, puisqu'elle
 * se choisit dans l'outil du client ; rien de date n'est donc enregistre.
 */
export async function recordMeetingBookedOnLink(prospectId: string): Promise<BookedOnLinkResult> {
  const raw = await apiClient.post<unknown>('/integrations/meetings/booked-on-link', { prospect_id: prospectId });
  return BookedOnLinkResultSchema.parse(raw);
}

export const CrmReadinessSchema = z.object({
  /** Un CRM est branche ET capable de recevoir ce rendez-vous, maintenant. */
  ready: z.boolean(),
  provider: z.string(),
});

export type CrmReadiness = z.infer<typeof CrmReadinessSchema>;

/**
 * 🚨 Ce que le serveur repond, pas ce que la fiche du client annonce.
 *
 * Un client declare sur le CRM Swoft mais sans connexion vivante voyait
 * s'ouvrir le calendrier, puis un refus (Kevin, 2026-09-30). Le geste se
 * decide sur cette reponse.
 */
export async function getCrmReadiness(customerId: string, prospectPhone = ''): Promise<CrmReadiness> {
  const params = new URLSearchParams();
  if (customerId) params.set('customer_id', customerId);
  if (prospectPhone) params.set('prospect_phone', prospectPhone);
  const raw = await apiClient.get<unknown>(`/integrations/meetings/crm-ready?${params.toString()}`);
  return CrmReadinessSchema.parse(raw);
}

export const MeetingProspectSchema = z.object({
  id: z.string(),
  customer_id: z.string(),
  prospect_name: z.string(),
  prospect_company: z.string(),
  prospect_phone: z.string(),
  /** The list the prospect was imported in. */
  source: z.string(),
  last_call_at: z.string(),
  /** A finished call can carry the meeting to the CRM, or the link mark. */
  has_finished_call: z.boolean(),
});

export type MeetingProspect = z.infer<typeof MeetingProspectSchema>;

/**
 * "Nouveau RDV" (Kevin, 2026-10-07): the prospect is searched in every list
 * of the chosen clients, not only in the queue the dialer has loaded. At
 * most 50, most recently called first.
 */
export async function searchMeetingProspects(input: { customerIds: string[]; query: string }): Promise<MeetingProspect[]> {
  const params = new URLSearchParams();
  if (input.customerIds.length > 0) params.set('customer_ids', input.customerIds.join(','));
  if (input.query.trim()) params.set('q', input.query.trim());
  const raw = await apiClient.get<unknown>(`/integrations/meetings/prospects?${params.toString()}`);
  return z.object({ prospects: z.array(MeetingProspectSchema) }).parse(raw).prospects;
}
